import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';

import { ThemeProvider } from '@/components/layout/ThemeProvider';
import { APP_MODE, APP_SHORT_NAME } from '@/config/branding';
import { routeTree } from './routeTree.gen';
import { registerUnauthorizedHandler } from '@/api/client';
import { applyPosViewport, enablePosTouchMode } from '@/features/pos/touchSupport';

import '@/styles/globals.css';

// Seteamos el <title> de la pestana con la marca corporativa.
document.title = `SDI · ${APP_SHORT_NAME}`;

// El cliente POS se usa en tablets: desactivamos el zoom por touch y
// aplicamos touch-action: manipulation. El tap tactil de botones dentro de
// contenedores con scroll lo maneja touchTapHandlers (onTouchEnd nativo).
if (APP_MODE === 'pos') {
  applyPosViewport();
  enablePosTouchMode();
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
    },
    mutations: {
      retry: false,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: { queryClient },
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Registrar handler de 401 que navega via SPA (no window.location.href).
// window.location.href causa full reload que pierde el cache de TanStack Query.
// Aqui es donde tenemos acceso al router context.
registerUnauthorizedHandler(() => {
  const loginRoute = window.location.pathname.startsWith('/master') ? '/master/login' : '/login';

  // Solo navegar si no estamos ya en el login correcto (evitar loops en errores de /me).
  if (window.location.pathname !== loginRoute) {
    void router.navigate({ to: loginRoute });
  }
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Elemento #root no encontrado en el DOM.');

createRoot(rootEl).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster richColors position="top-right" closeButton />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
